// src/pages/Gallery.js - Redesigned and Enhanced Version
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Gallery.css';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExpand, 
  faTimes, 
  faChevronLeft, 
  faChevronRight,
  faSpinner,
  faTools,
  faHammer,
  faHardHat,
  faHome,
  faHistory,
  faMapMarkerAlt,
  faCalendarAlt,
  faBuilding,
  faFilter,
  faThumbsUp,
  faCheck,
  faInfoCircle,
  faStar
} from '@fortawesome/free-solid-svg-icons';

// Gallery data will be fetched from the backend
const initialGallery = [];

const Gallery = () => {
  // State for filtered images
  const [filteredImages, setFilteredImages] = useState([]);
  // All gallery data
  const [galleryData, setGalleryData] = useState(initialGallery);
  // State for active filter
  const [activeFilter, setActiveFilter] = useState('all');
  // State for visible images (for load more functionality)
  const [visibleCount, setVisibleCount] = useState(6);
  // State for fullscreen mode
  const [fullscreen, setFullscreen] = useState(false);
  // State for current fullscreen image index
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // State for loading images
  const [loading, setLoading] = useState(true);
  // State for animation timing
  const [isAnimating, setIsAnimating] = useState(false);
  // Ref for fullscreen modal
  const modalRef = useRef(null);
  // State for sorting option
  const [sortOption, setSortOption] = useState('newest');
  // State for section animations
  const [animated, setAnimated] = useState(false);

  // Filter categories
  const categories = [
    { id: 'all', label: 'All Projects', icon: faHome },
    { id: 'installations', label: 'New Installations', icon: faHardHat },
    { id: 'repairs', label: 'Repairs & Maintenance', icon: faTools },
    { id: 'traditional', label: 'Traditional Roofing', icon: faHistory }
  ];

  // Sort options
  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'alphabetical', label: 'A-Z' }
  ];

  // Fetch gallery data on mount
  useEffect(() => {
    fetch('/api/gallery')
      .then(res => res.json())
      .then(data => {
        setGalleryData(data);
      })
      .catch(err => {
        console.error('Failed to load gallery data', err);
      });
  }, []);

  // Load and filter images
  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      let sorted = [...galleryData];
      
      // Apply sorting
      switch (sortOption) {
        case 'newest':
          sorted.sort((a, b) => parseInt(b.year) - parseInt(a.year));
          break;
        case 'oldest':
          sorted.sort((a, b) => parseInt(a.year) - parseInt(b.year));
          break;
        case 'rating':
          sorted.sort((a, b) => b.rating - a.rating);
          break;
        case 'alphabetical':
          sorted.sort((a, b) => a.title.localeCompare(b.title));
          break;
        default:
          break;
      }
      
      // Apply category filter
      if (activeFilter === 'all') {
        setFilteredImages(sorted);
      } else {
        const filtered = sorted.filter(item => item.category === activeFilter);
        setFilteredImages(filtered);
      }
      
      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [activeFilter, sortOption, galleryData]);

  // Animation on scroll
  useEffect(() => {
    const handleScroll = () => {
      const introElement = document.querySelector('.gallery-intro');
      if (!introElement) return;
      
      const position = introElement.getBoundingClientRect();
      if (position.top < window.innerHeight * 0.75 && !animated) {
        setAnimated(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial load
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [animated]);

  // Handle filter click
  const handleFilterClick = (category) => {
    if (category === activeFilter) return;
    
    setIsAnimating(true);
    setActiveFilter(category);
    setVisibleCount(6); // Reset visible count on filter change
    setLoading(true);
    
    // Reset animation state after transition completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  // Handle sort change
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
    setLoading(true);
  };

  // Handle load more
  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 6);
  };

  // Open fullscreen image
  const openFullscreen = (index) => {
    setCurrentImageIndex(index);
    setFullscreen(true);
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  };

  // Close fullscreen image
  const closeFullscreen = () => {
    setFullscreen(false);
    // Restore body scroll
    document.body.style.overflow = 'auto';
  };

  // Navigate to previous image in fullscreen
  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? filteredImages.length - 1 : prev - 1
    );
  }, [filteredImages.length]);

  // Navigate to next image in fullscreen
  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => 
      prev === filteredImages.length - 1 ? 0 : prev + 1
    );
  }, [filteredImages.length]);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fullscreen && modalRef.current && !modalRef.current.contains(e.target)) {
        closeFullscreen();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [fullscreen]);

  // Handle keyboard navigation in fullscreen mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!fullscreen) return;

      switch (e.key) {
        case 'Escape':
          closeFullscreen();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreen, prevImage, nextImage]);

  // Render star ratings
  const renderStars = (rating) => {
    return (
      <div className="stars-container">
        {[...Array(5)].map((_, i) => (
          <FontAwesomeIcon 
            key={i} 
            icon={faStar} 
            className={i < Math.floor(rating) ? 'star filled' : 'star'}
          />
        ))}
        <span className="rating-number">{rating}</span>
      </div>
    );
  };

  return (
    <div className="gallery-page">
      {/* Gallery Page Header */}
      <div className="gallery-header">
        <div className="container">
          <div className={`page-title-wrapper ${animated ? 'animated' : ''}`}>
            <h1 className="page-title">Our Domestic Projects</h1>
            <div className="title-separator"></div>
            <p className="page-subtitle">
              Browse our collection of roofing projects showcasing our craftsmanship and attention to detail
            </p>
          </div>
        </div>
      </div>
      
      <div className="gallery-content">
        <div className="container">
          <div className={`gallery-intro ${animated ? 'animated' : ''}`}>
            <div className="intro-icon">
              <FontAwesomeIcon icon={faHardHat} />
            </div>
            <h2>Our Craftsmanship</h2>
            <p>
              At Old Glossop Roofing, we take pride in our traditional roofing work. 
              Each project is approached with a deep respect for historic techniques and materials, 
              ensuring that our work maintains the character and integrity of every property.
            </p>
              <p>
                Browse our domestic projects to see examples of our work across Glossop and surrounding areas.
              </p>
          </div>

          {/* Filter and Sort Section */}
          <div className={`filters-section ${animated ? 'animated' : ''}`}>
            {/* Filter buttons */}
            <div className="gallery-filters">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`gallery-filter ${activeFilter === category.id ? 'active' : ''}`}
                  onClick={() => handleFilterClick(category.id)}
                  disabled={isAnimating}
                >
                  <FontAwesomeIcon icon={category.icon} className="filter-icon" />
                  <span>{category.label}</span>
                </button>
              ))}
            </div>
            
            {/* Sort dropdown */}
            <div className="sorting-container">
              <div className="sort-label">
                <FontAwesomeIcon icon={faFilter} />
                <span>Sort By:</span>
              </div>
              <select 
                className="sort-select" 
                value={sortOption} 
                onChange={handleSortChange}
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="gallery-loading">
              <div className="spinner-container">
                <FontAwesomeIcon icon={faSpinner} spin className="spinner" />
              </div>
              <p>Loading projects...</p>
            </div>
          ) : (
            <>
              {filteredImages.length > 0 ? (
                <>
                  <div className={`gallery-grid ${isAnimating ? 'fade-out' : 'fade-in'}`}>
                    {filteredImages.slice(0, visibleCount).map((item, index) => (
                      <div className="gallery-item" key={item.id}>
                        <div className="gallery-img-container">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="gallery-img"
                            loading="lazy"
                          />
                          <div className="project-completed">
                            <FontAwesomeIcon icon={faCheck} />
                            <span>Completed</span>
                          </div>
                          <div className="gallery-overlay">
                            <div className="overlay-content">
                              <h3 className="gallery-item-title">{item.title}</h3>
                              <div className="gallery-item-details">
                                <span className="gallery-location">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} /> {item.location}
                                </span>
                                <span className="gallery-year">
                                  <FontAwesomeIcon icon={faCalendarAlt} /> {item.year}
                                </span>
                              </div>
                              <div className="gallery-rating">
                                {renderStars(item.rating)}
                              </div>
                              <p className="gallery-item-description">{item.description}</p>
                              <button
                                className="gallery-expand-btn"
                                onClick={() => openFullscreen(index)}
                                aria-label="View larger image"
                              >
                                <FontAwesomeIcon icon={faExpand} />
                                <span>View Larger</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Load More Button */}
                  {visibleCount < filteredImages.length && (
                    <div className="gallery-load-more">
                      <button className="load-more-btn" onClick={handleLoadMore}>
                        Load More Projects
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="gallery-no-results">
                  <div className="no-results-icon">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </div>
                  <h3>No projects found in this category</h3>
                  <p>Please try selecting a different category or view all projects.</p>
                  <button 
                    className="gallery-filter active" 
                    onClick={() => handleFilterClick('all')}
                  >
                    <FontAwesomeIcon icon={faHome} className="filter-icon" />
                    <span>View All Projects</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Request Quote Call-to-Action */}
      <div className="gallery-cta">
        <div className="cta-pattern"></div>
        <div className="container">
          <div className="cta-content">
            <div className="cta-icon">
              <FontAwesomeIcon icon={faThumbsUp} />
            </div>
            <h2>Inspired by our craftsmanship?</h2>
            <p>Contact us today to discuss your roofing project and request a free, no-obligation quote.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="cta-btn primary">Get a Free Quote</Link>
              <Link to="/services" className="cta-btn secondary">View Our Services</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {fullscreen && (
        <div className="gallery-modal open">
          <div className="modal-content" ref={modalRef}>
            <button
              className="modal-close"
              onClick={closeFullscreen}
              aria-label="Close fullscreen"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <button
              className="modal-prev"
              onClick={prevImage}
              aria-label="Previous image"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="modal-image-container">
              <img
                src={filteredImages[currentImageIndex].image}
                alt={filteredImages[currentImageIndex].title}
                className="modal-image"
              />
              <div className="modal-caption">
                <h3>{filteredImages[currentImageIndex].title}</h3>
                <div className="caption-details">
                  <span className="caption-location">
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> {filteredImages[currentImageIndex].location}
                  </span>
                  <span className="caption-year">
                    <FontAwesomeIcon icon={faCalendarAlt} /> {filteredImages[currentImageIndex].year}
                  </span>
                  <span className="caption-rating">
                    {renderStars(filteredImages[currentImageIndex].rating)}
                  </span>
                </div>
                <p>{filteredImages[currentImageIndex].description}</p>
              </div>
            </div>
            <button
              className="modal-next"
              onClick={nextImage}
              aria-label="Next image"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            
            <div className="modal-counter">
              {currentImageIndex + 1} / {filteredImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;